import React, { Component } from "react";
import { connect } from "react-redux";
import axios, { CancelToken } from "axios";
import { Translate, I18n } from "react-redux-i18n";
import _ from "lodash";
import ControlRow from "../utils/components/ControlRow";

import { setAlert } from "../../actions/alertActions";
import {
  searchModule,
  getPlug,
  getChargerStatus,
  getPlugStatus,
} from "../utils/Utils";
import Infrastructure from "../utils/classes/Infrastructure";

import "../../styles/utils/_loadingScreen.scss";
import "../../styles/components/chargers/EnergyManagement.scss";
import no_chargers from "../../styles/images/no_chargers.svg";
import evio_busy from "../../styles/images/markers/marker_evio_busy.svg";
import marker_private_chargers from "../../styles/images/markers/marker_private_chargers.svg";
import marker_private_offline_chargers from "../../styles/images/markers/marker_private_offline_chargers.svg";
import save from "../../styles/images/save.svg";

//importing green/red checkmarks and reload
import yes from "../../styles/images/yes.png";
import no from "../../styles/images/no.png";

const controlTypes = Object.freeze({
  AUTO: "AUTO",
  MANUAL: "MANUAL",
  OFF: "OFF",
  BOOST: "BOOST",
});

class EnergyManagement extends Component {
  constructor(props) {
    super(props);
    this._mounted = false;
    this.cancel = null;
    this.componentRef = React.createRef();
    this.popupRef = React.createRef();

    this.state = {
      loading: true,
      search: "",
      places: [],
      selectedPlace: null,
      selectedCharger: null,
      selectedPlug: null,

      switchBoardList: undefined,
      selectedSwitchBoardIndex: 1,
      switchBoardModes: undefined,
      lastUpdateTime: undefined,
      selectedOptions: {},
      currentFix: {},
      powerMin: {},
      isPowerMin: {},
      isCurrentFix: {},
    };

    this.fetchPlaces = this.fetchPlaces.bind(this);
    this.handleOnSelect = this.handleOnSelect.bind(this);
    this.formatDate = this.formatDate.bind(this);
    this.refreshContent = this.refreshContent.bind(this);
    this.swapMode = this.swapMode.bind(this);
    this.updateArrayById = this.updateArrayById.bind(this);
    this.updateStatus = this.updateStatus.bind(this);
  }

  componentDidMount() {
    this._mounted = true;
    this.fetchPlaces();
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this.cancel) this.cancel("Component is unmounting");
  }

  updateStatus() {
    let formattedDate = this.formatDate();
    this.setState({ lastUpdateTime: formattedDate });
  }

  formatDate() {
    const dateStr = this.state.selectedPlace?.lastUpdateStatus;
    if (dateStr) {
      const date = new Date(dateStr);

      // Format date part using toLocaleDateString()
      const formattedDate = new Intl.DateTimeFormat("pt-PT", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      }).format(date);

      // Format time part using Intl.DateTimeFormat for 24-hour format
      const formattedTime = new Intl.DateTimeFormat("pt-PT", {
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        hour12: false,
      }).format(date);

      return `${formattedDate} ${formattedTime}`;
    } else {
      return "unavailable";
    }
  }

  swapMode(selectedOption) {
    //this.safeSetState({ loading: true });

    let chargingMode = selectedOption;
    let switchBoardModes = {
      "Base Mode": "chargingMode_Base_Mode",
      "Solar Mode": "chargingMode_Solar_Mode",
    };

    let keyForChargingMode = Object.keys(switchBoardModes).find(
      (key) => switchBoardModes[key] === chargingMode
    );

    const requestBody = {
      switchBoardId: this.state.selectedPlace.id,
      chargingMode: keyForChargingMode,
    };

    axios
      .patch("/api/private/switchboards/updateChargingMode", requestBody, {
        headers: {
          userid: this.props.accountData.id,
        },
        cancelToken: new CancelToken((c) => (this.cancel = c)),
      })
      .then((res) => {
        const idToUpdate = res.data.switchBoard[0].id;
        const updatedObject = res.data.switchBoard[0];

        this.updateArrayById(idToUpdate, updatedObject);
        this.props.setAlert(I18n.t("general_updatedSuccessfully"), "primary");
        this.updateStatus();
      })
      .then(() => {
        // Set loading to false after all setState calls are complete
        //this.safeSetState({ loading: false });
        //this.refreshContent();
      })
      .catch((err) => {
        console.error(err);
      });
  }

  refreshContent = () => {
    this.safeSetState({ loading: true });

    if (this.state.selectedPlace !== undefined) {
      axios
        .get("/api/private/switchboards/mySwitchboards", {
          headers: {
            switchboardid: this.state.selectedPlace.id,
            userid: this.props.accountData.id,
          },
          cancelToken: new axios.CancelToken((c) => (this.cancel = c)),
        })
        .then((res) => {
          const idToUpdate = res.data.switchBoardList[0].id;
          const updatedObject = res.data.switchBoardList[0];

          this.updateArrayById(idToUpdate, updatedObject);
        })
        .then(() => {
          this.safeSetState({ loading: false });
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            console.log("Request canceled", err.message);
          } else {
            console.error(err);
          }
        });
    }
  };

  updateArrayById = (idToUpdate, updatedObject) => {
    // Create a new array with updated values while ensuring the structure of the infrastructure object is maintained
    const updatedPlaces = this.state.places.map((infrastructure) => {
      if (infrastructure.id === idToUpdate) {
        // Create a new Infrastructure instance to maintain the correct type
        return new Infrastructure({
          ...infrastructure,
          ...updatedObject,
        });
      }
      return infrastructure;
    });

    // Batch state updates
    this.setState(
      (prevState) => ({
        places: updatedPlaces,
        selectedPlace:
          prevState.selectedPlace && prevState.selectedPlace.id === idToUpdate
            ? new Infrastructure({
                ...prevState.selectedPlace,
                ...updatedObject,
              })
            : prevState.selectedPlace,
      }),
      this.updateStatus
    );
  };

  fetchPlaces() {
    axios
      .get("/api/private/switchboards/mySwitchboards", {
        headers: {
          userid: this.props.accountData.id,
        },
        cancelToken: new CancelToken((c) => (this.cancel = c)),
      })
      .then((res) => {
        const switchBoardList = res.data.switchBoardList;
        this.safeSetState({ switchBoardModes: res.data.chargingModes });
        const places = switchBoardList.map((p) => new Infrastructure(p));
        // Find selectedPlace based on the existing logic
        let selectedPlace = this.state.selectedPlace;
        if (places.length > 0) {
          let idx = -1;
          if (selectedPlace)
            idx = places.findIndex((p) => p._id === selectedPlace._id);
          selectedPlace = idx !== -1 ? places[idx] : places[0];
        }

        // Use Promise.all to wait for all setState calls to complete
        return Promise.all([
          new Promise((resolve) =>
            this.safeSetState({ switchBoardList }, resolve)
          ),
          new Promise((resolve) => this.safeSetState({ places }, resolve)),
          new Promise((resolve) =>
            this.safeSetState({ selectedPlace }, resolve)
          ),
        ]);
      })
      .then(() => {
        this.updateStatus();
        // Set loading to false after all setState calls are complete
        this.safeSetState({ loading: false });
      })
      .catch((err) => console.error(err));
  }

  parseStatus(chargerStatus, plugStatus, operationalStatus) {
    if (getChargerStatus(chargerStatus) === "online") {
      switch (getPlugStatus(plugStatus)) {
        case "available": {
          return operationalStatus === "WAITINGAPROVAL" ? (
            <>
              <img
                src={
                  this.props.brand?.icons?.mapIcons?.[
                    `marker_${_.camelCase(
                      this.props.brand?.brandName
                    )}_available`
                  ] || marker_private_chargers
                }
                alt=""
              />
              <div>
                <Translate value="myChargers_available" />
                <p>
                  <Translate value="myChargers_waiting_approval" />
                </p>
              </div>
            </>
          ) : (
            <>
              <img
                src={
                  this.props.brand?.icons?.mapIcons?.[
                    `marker_${_.camelCase(
                      this.props.brand?.brandName
                    )}_available`
                  ] || marker_private_chargers
                }
                alt=""
              />
              <Translate value="myChargers_available" />
            </>
          );
        }
        case "in-use": {
          return operationalStatus === "WAITINGAPROVAL" ? (
            <>
              <img
                src={
                  this.props.brand?.icons?.mapIcons?.[
                    `marker_${_.camelCase(this.props.brand?.brandName)}_busy`
                  ] || evio_busy
                }
                alt=""
              />
              <div>
                <Translate value="myChargers_inUse" />
                <p>
                  <Translate value="myChargers_waiting_approval" />
                </p>
              </div>
            </>
          ) : (
            <>
              <img
                src={
                  this.props.brand?.icons?.mapIcons?.[
                    `marker_${_.camelCase(this.props.brand?.brandName)}_busy`
                  ] || evio_busy
                }
                alt=""
              />
              <Translate value="myChargers_inUse" />
            </>
          );
        }
        case "unavailable": {
          return operationalStatus === "WAITINGAPROVAL" ? (
            <>
              <img
                src={
                  this.props.brand?.icons?.mapIcons?.[
                    `marker_${_.camelCase(this.props.brand?.brandName)}_offline`
                  ] || marker_private_offline_chargers
                }
                alt=""
              />
              <div>
                <Translate value="myChargers_offline" />
                <p>
                  <Translate value="myChargers_waiting_approval" />
                </p>
              </div>
            </>
          ) : (
            <>
              <img
                src={
                  this.props.brand?.icons?.mapIcons?.[
                    `marker_${_.camelCase(this.props.brand?.brandName)}_offline`
                  ] || marker_private_offline_chargers
                }
                alt=""
              />
              <Translate value="myChargers_offline" />
            </>
          );
        }
        default:
          return operationalStatus === "WAITINGAPROVAL" ? (
            <>
              <img
                src={
                  this.props.brand?.icons?.mapIcons?.[
                    `marker_${_.camelCase(this.props.brand?.brandName)}_offline`
                  ] || marker_private_offline_chargers
                }
                alt=""
              />
              <div>
                <Translate value="myChargers_unknown" />
                <p>
                  <Translate value="myChargers_waiting_approval" />
                </p>
              </div>
            </>
          ) : (
            <>
              <img
                src={
                  this.props.brand?.icons?.mapIcons?.[
                    `marker_${_.camelCase(this.props.brand?.brandName)}_offline`
                  ] || marker_private_offline_chargers
                }
                alt=""
              />
              <Translate value="myChargers_unknown" />
            </>
          );
      }
    } else {
      return operationalStatus === "WAITINGAPROVAL" ? (
        <>
          <img
            src={
              this.props.brand?.icons?.mapIcons?.[
                `marker_${_.camelCase(this.props.brand?.brandName)}_offline`
              ] || marker_private_offline_chargers
            }
            alt=""
          />
          <div>
            <Translate value="myChargers_offline" />
            <p>
              <Translate value="myChargers_waiting_approval" />
            </p>
          </div>
        </>
      ) : (
        <>
          <img
            src={
              this.props.brand?.icons?.mapIcons?.[
                `marker_${_.camelCase(this.props.brand?.brandName)}_offline`
              ] || marker_private_offline_chargers
            }
            alt=""
          />
          <Translate value="myChargers_offline" />
        </>
      );
    }
  }

  handleOnSelect(selectedPlace) {
    this.safeSetState({
      selectedPlace,
    });
    this.updateStatus();
  }

  getPopupRef() {
    return this.popupRef;
  }

  setModal(modal, bool, callback) {
    let newModals = Object.assign({}, this.state.modals);
    newModals[modal] = bool;
    this.safeSetState(
      {
        modals: newModals,
      },
      callback
    );
  }

  checkPlug(p) {
    return (
      p.connectorType &&
      p.amperage &&
      p.power &&
      p.voltage &&
      p.active &&
      p.connectorType !== "" &&
      p.amperage !== "" &&
      p.power !== "" &&
      p.voltage !== "" &&
      p.active !== "" &&
      p.connectorType !== "Unknown"
    );
  }

  checkTariff(plug, charger) {
    if (
      charger.accessType === "Private" ||
      charger.accessType === "FreeCharge"
    ) {
      return true;
    }

    if (!plug.tariff.length > 0) {
      return false;
    }

    let bool = true;

    plug.tariff.forEach((p) => {
      if (
        !(
          (p.tariffType &&
            p.groupName &&
            p.tariff &&
            p.name &&
            p.name !== "" &&
            p.tariffType !== "" &&
            p.groupName !== "" &&
            p.tariffType !== "Unknown") ||
          (p.tariffType &&
            p.fleetName &&
            p.tariff &&
            p.name &&
            p.name !== "" &&
            p.tariffType !== "" &&
            p.fleetName !== "" &&
            p.tariffType !== "Unknown")
        )
      ) {
        bool = false;
      }
    });
    return bool;
  }

  safeSetState(params, callback) {
    if (this._mounted) this.setState(params, callback);
  }

  manageChargerApproval(adminOrder) {
    const patch = {
      chargerId: this.state.selectedCharger.chargerId,
      operationalStatus: adminOrder,
    };

    axios
      .patch("/api/private/chargers/operationalStatus", patch, {
        cancelToken: new CancelToken((c) => (this.cancel = c)),
      })
      .then((res) => {
        if (res.status === 200) {
          this.refreshContent();
          this.props.setAlert(I18n.t("Charger status saved"), "primary");
          this.safeSetState({ selectedCharger: null });
        }
      })
      .catch((err) => console.error(err));
  }

  handleSubmitControlType = (controlType, plug, index, charger) => {
    let patch;

    if (controlType !== controlTypes.MANUAL) {
      patch = {
        hwId: charger.hwId,
        plugId: plug.plugId,
        userId: this.props.accountData.id,
        controlType: controlType,
      };
    } else {
      patch = {
        hwId: charger.hwId,
        plugId: plug.plugId,
        userId: this.props.accountData.id,
        controlType: controlType,
        minActivePower: this.state.powerMin[index] * 1000,
        setCurrentLimit: Number(this.state.currentFix[index]),
      };
    }

    axios
      .patch("/api/private/chargers/plugs/v1/setpoints", patch)
      .then((res) => {
        if (res.status === 200) {
          this.props.setAlert(I18n.t(res.data.message), "primary");
        }
      })
      .catch((err) => {
        if (err.response) {
          const isJson =
            err.response.headers["content-type"]?.includes("application/json");
          if (isJson && err.response.data.code) {
            this.props.setAlert(I18n.t(err.response.data.message), "danger");
          } else {
            this.props.setAlert(I18n.t("general_errorMessage"), "danger");
          }
        }
      });
  };

  handleOptionChange = (e, rowIndex, plug, charger) => {
    const { value } = e.target;

    this.safeSetState((prevState) => ({
      ...prevState,
      selectedOptions: {
        ...prevState.selectedOptions,
        [rowIndex]: value,
      },
      isPowerMin: {
        ...prevState.isPowerMin,
        [rowIndex]: value === controlTypes.MANUAL,
      },
      isCurrentFix: {
        ...prevState.isCurrentFix,
        [rowIndex]: value === controlTypes.MANUAL,
      },
    }));

    if (value !== controlTypes.MANUAL) {
      this.handleSubmitControlType(value, plug, rowIndex, charger);
    }
  };

  handleCurrentFixChange = (e, rowIndex) => {
    const { value } = e.target;

    if (
      value === "" ||
      (/^\d*\.?\d{0,2}$/.test(value) && parseFloat(value) >= 0)
    ) {
      this.safeSetState((prevState) => ({
        ...prevState,
        currentFix: {
          ...prevState.currentFix,
          [rowIndex]: value,
        },
      }));
    }
  };

  handlePowerMinChange = (e, rowIndex) => {
    const { value } = e.target;

    if (
      value === "" ||
      (/^\d*\.?\d{0,2}$/.test(value) && parseFloat(value) >= 0)
    ) {
      this.safeSetState((prevState) => ({
        ...prevState,
        powerMin: {
          ...prevState.powerMin,
          [rowIndex]: value,
        },
      }));
    }
  };

  renderCurrentFix(chargerIndex, plug) {
    if (
      Object.keys(this.state.currentFix).length === 0 &&
      plug?.balancingInfo?.setCurrentLimit
    ) {
      this.safeSetState((prevState) => ({
        ...prevState,
        currentFix: {
          ...prevState.currentFix,
          [chargerIndex]: (plug?.balancingInfo?.setCurrentLimit).toFixed(1),
        },
      }));
    }

    return (
      <>
        {this.state.isCurrentFix[chargerIndex] ||
        this.state.selectedOptions[chargerIndex] === controlTypes.MANUAL ? (
          <input
            type="number"
            value={this.state.currentFix[chargerIndex]}
            onChange={(e) => this.handleCurrentFixChange(e, chargerIndex)}
            style={{ width: "100%" }}
            min="0"
          />
        ) : (
          <p>
            {this.state.currentFix[chargerIndex]
              ? this.state.currentFix[chargerIndex]
              : plug?.balancingInfo?.setCurrentLimit
              ? (plug?.balancingInfo?.setCurrentLimit).toFixed(1)
              : "-"}
          </p>
        )}
      </>
    );
  }

  renderPowerMin(chargerIndex, plug) {
    if (
      Object.keys(this.state.powerMin).length === 0 &&
      plug?.balancingInfo?.minActivePower
    ) {
      this.safeSetState((prevState) => ({
        ...prevState,
        powerMin: {
          ...prevState.powerMin,
          [chargerIndex]: (plug?.balancingInfo?.minActivePower / 1000).toFixed(
            1
          ),
        },
      }));
    }

    return (
      <>
        {this.state.isPowerMin[chargerIndex] ||
        this.state.selectedOptions[chargerIndex] === controlTypes.MANUAL ? (
          <input
            type="number"
            value={this.state.powerMin[chargerIndex]}
            onChange={(e) => this.handlePowerMinChange(e, chargerIndex)}
            style={{ width: "100%" }}
            min="0"
          />
        ) : (
          <p>
            {this.state.powerMin[chargerIndex]
              ? this.state.powerMin[chargerIndex]
              : plug?.balancingInfo?.minActivePower
              ? (plug?.balancingInfo?.minActivePower / 1000).toFixed(1)
              : "-"}
          </p>
        )}
      </>
    );
  }

  renderControlType(chargerIndex, plug, charger) {
    const options = [
      controlTypes.AUTO,
      controlTypes.MANUAL,
      controlTypes.OFF,
      controlTypes.BOOST,
    ];

    if (
      Object.keys(this.state.selectedOptions).length === 0 &&
      plug?.balancingInfo?.controlType
    ) {
      this.safeSetState((prevState) => ({
        ...prevState,
        selectedOptions: {
          ...prevState.selectedOptions,
          [chargerIndex]: plug?.balancingInfo?.controlType,
        },
      }));
    }

    return (
      <div className="select-section">
        <select
          value={
            this.state.selectedOptions &&
            this.state.selectedOptions[chargerIndex]
              ? this.state.selectedOptions[chargerIndex]
              : plug?.balancingInfo?.controlType
          }
          onChange={(e) =>
            this.handleOptionChange(e, chargerIndex, plug, charger)
          }
          disabled={!plug?.balancingInfo?.controlType && true}
        >
          <option value="" disabled>
            Select an option
          </option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        {this.state.selectedOptions &&
          this.state.selectedOptions[chargerIndex] === controlTypes.MANUAL && (
            <button
              className="save-btn"
              disabled={
                !plug?.balancingInfo?.controlType ||
                (Object.keys(this.state.powerMin).length === 0 &&
                  Object.keys(this.state.currentFix).length === 0)
              }
              onClick={() =>
                this.handleSubmitControlType(
                  this.state.selectedOptions[chargerIndex],
                  plug,
                  chargerIndex,
                  charger
                )
              }
            >
              <img
                src={save}
                alt="save"
                style={{
                  opacity:
                    !plug?.balancingInfo?.controlType ||
                    (Object.keys(this.state.powerMin).length === 0 &&
                      Object.keys(this.state.currentFix).length === 0)
                      ? 0.5
                      : 1,
                }}
              />
            </button>
          )}
      </div>
    );
  }

  render() {
    const formattedDate = !this.state.loading
      ? this.formatDate(this.state.selectedPlace?.lastUpdateStatus)
      : null;
    const stateIsOnline = !this.state.loading
      ? this.state.selectedPlace?.online
      : false;

    return !this.state.loading ? (
      <>
        <div id="energyManagement" className="main-container">
          {this.state.switchBoardList.length > 0 && (
            <div className="control-bar">
              <ControlRow
                showSearch={false}
                search={this.state.search}
                onSearchChange={(e) =>
                  searchModule.setSearch(this, e.target.value)
                }
                showCarousel={this.state.selectedPlace !== null}
                carousel={{
                  items: this.state.places,
                  selected: this.state.selectedPlace
                    ? this.state.places.findIndex(
                        (i) => i.id === this.state.selectedPlace.id
                      )
                    : 0,
                  onSelect: this.handleOnSelect,
                }}
                showRefresh={true}
                refreshContent={this.refreshContent}
                showSwapMode={true}
                swapModeFunct={this.swapMode}
                switchBoardModes={this.state.switchBoardModes}
                currentMode={this.state.selectedPlace?.chargingMode}
                places={this.state.places}
                selectedPlace={this.state.selectedPlace}
                refreshData={this.refreshContent}
              />
            </div>
          )}
          {this.state.places.length === 0 && (
            <div className="no-places">
              <img src={no_chargers} alt="" className="back-car" />
              <p>
                <Translate value="chargers_noPlacesText" />
              </p>
            </div>
          )}
          {this.state.switchBoardList.length > 0 && (
            <>
              <div className="component-container">
                <div className="table-container">
                  <div className="table-header">
                    <div className="row no-gutters">
                      <div className="col-12 row no-gutters titles">
                        <div className="col-2 title">
                          <Translate value="charger_EnergyManagement" />
                        </div>
                        <div className="col-1 title">
                          <Translate value="connector_EnergyManagement" />
                        </div>
                        <div className="col title">
                          <Translate value="state_EnergyManagement" />
                        </div>
                        <div className="col-1 title">
                          <Translate value="energyManagement_EnergyManagement" />
                        </div>
                        <div className="col-1 title">
                          <Translate value="tension_EnergyManagement" /> (V)
                        </div>
                        <div className="col-1 title">
                          <Translate value="current_EnergyManagement" /> (A)
                        </div>
                        <div className="col-1 title">
                          <Translate value="current_fix_EnergyManagement" />
                        </div>
                        <div className="col-1 title">
                          <Translate value="potency_EnergyManagement" /> (kW)
                        </div>
                        <div className="col-1 title">
                          <Translate value="potency_min_EnergyManagement" />
                        </div>
                        <div className="col-2 title">
                          <Translate value="control_type_EnergyManagement" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="table-values">
                    {this.state.selectedPlace?.listChargers.map(
                      (charger, chargerIndex) => {
                        if (charger.plugs?.length > 0) {
                          return charger.plugs.map((plug, idx) => {
                            return (
                              <div
                                key={`${chargerIndex}}`}
                                className="row no-gutters"
                              >
                                <div
                                  className="col-12 row no-gutters values slim"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                >
                                  <div className="col-2">
                                    <div className="charger-info-container">
                                      <p>{charger.name}</p>
                                      <p>{charger.hwId}</p>
                                    </div>
                                  </div>
                                  <div className="col-1">
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      {plug.connectorType ? (
                                        <>
                                          <img
                                            src={
                                              getPlug(plug.connectorType)
                                                ?.borderless
                                            }
                                            alt=""
                                          />
                                          <div className="plug-info-container">
                                            <p>
                                              {
                                                getPlug(plug.connectorType)
                                                  ?.name
                                              }
                                            </p>
                                            <p>{plug.plugId}</p>
                                          </div>
                                        </>
                                      ) : (
                                        <p>
                                          <Translate
                                            className="no-plugs"
                                            value="myChargers_plugNotConfigurated"
                                          />
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="col">
                                    <div>
                                      <p>
                                        {this.parseStatus(
                                          charger.status,
                                          plug.status,
                                          charger.operationalStatus
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="col-1">
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                      }}
                                    >
                                      <p>
                                        {charger.energyManagementEnable ? (
                                          <img
                                            src={yes}
                                            alt="Online"
                                            style={{
                                              width: "30px",
                                              height: "30px",
                                            }}
                                          />
                                        ) : (
                                          <img
                                            src={no}
                                            alt="Online"
                                            style={{
                                              width: "30px",
                                              height: "30px",
                                            }}
                                          />
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="col-1">
                                    <div>
                                      <p>
                                        {charger.energyManagementEnable ===
                                        false
                                          ? "-"
                                          : !plug?.balancingInfo ||
                                            Object.keys(plug?.balancingInfo)
                                              .length === 0
                                          ? "-"
                                          : plug?.balancingInfo?.voltage &&
                                            plug?.balancingInfo?.voltage.toFixed(
                                              0
                                            )}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="col-1">
                                    <div>
                                      <p>
                                        {charger.energyManagementEnable ===
                                        false
                                          ? "-"
                                          : !plug?.balancingInfo ||
                                            Object.keys(plug?.balancingInfo)
                                              .length === 0
                                          ? "-"
                                          : plug?.balancingInfo?.totalCurrent &&
                                            plug?.balancingInfo?.totalCurrent.toFixed(
                                              1
                                            )}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="col-1">
                                    <div>
                                      {charger.energyManagementEnable === false
                                        ? "-"
                                        : this.renderCurrentFix(
                                            chargerIndex,
                                            plug
                                          )}
                                    </div>
                                  </div>
                                  <div className="col-1">
                                    <div>
                                      <p>
                                        {charger.energyManagementEnable ===
                                        false
                                          ? "-"
                                          : !plug?.balancingInfo ||
                                            Object.keys(plug?.balancingInfo)
                                              .length === 0
                                          ? "-"
                                          : plug?.balancingInfo?.power &&
                                            (
                                              plug?.balancingInfo?.power / 1000
                                            ).toFixed(1)}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="col-1">
                                    <div>
                                      {charger.energyManagementEnable === false
                                        ? "-"
                                        : this.renderPowerMin(
                                            chargerIndex,
                                            plug
                                          )}
                                    </div>
                                  </div>
                                  <div className="col-2">
                                    {charger.energyManagementEnable === false
                                      ? "-"
                                      : this.renderControlType(
                                          chargerIndex,
                                          plug,
                                          charger
                                        )}
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        } else {
                          <div
                            key={`${chargerIndex}}`}
                            className="row no-gutters"
                          >
                            <div
                              className="col-12 row no-gutters values slim"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <div className="col">
                                <div className="charger-info-container">
                                  <p>{charger.name}</p>
                                  <p>{charger.hwId}</p>
                                </div>
                              </div>
                              <div className="col">
                                <div>-</div>
                              </div>
                              <div className="col table-flex">
                                <div className="col table-flex">
                                  <p>
                                    {this.parseStatus(
                                      charger.status,
                                      null,
                                      charger.operationalStatus
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="col">
                                <div>
                                  <p>
                                    {charger.energyManagementEnable
                                      ? "true"
                                      : "false"}
                                  </p>
                                </div>
                              </div>
                              <div className="col">
                                <div>
                                  <p>-</p>
                                </div>
                              </div>
                              <div className="col">
                                <div>
                                  <p>-</p>
                                </div>
                              </div>
                              <div className="col">
                                <div>
                                  <p>-</p>
                                </div>
                              </div>
                              <div className="col">
                                <div>
                                  <p>-</p>
                                </div>
                              </div>
                              <div className="col">
                                <div>
                                  <p>-</p>
                                </div>
                              </div>
                              <div className="col">
                                <div>
                                  <p>-</p>
                                </div>
                              </div>
                            </div>
                          </div>;
                        }
                      }
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          {this.state.switchBoardList.length > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ fontSize: "20px" }}>
                <b>
                  <Translate value="lastValueUpdate_EnergyManagement" />
                </b>{" "}
                {formattedDate}
              </div>
              <div style={{ fontSize: "20px" }}>
                <b>
                  <Translate value="connectionState_EnergyManagement" />
                </b>{" "}
                {stateIsOnline ? (
                  <img
                    src={yes}
                    alt="Online"
                    style={{ width: "20px", height: "20px" }}
                  />
                ) : (
                  <img
                    src={no}
                    alt="Offline"
                    style={{ width: "20px", height: "20px" }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </>
    ) : (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="generic-loading">
            <div></div>
            <div></div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  accountData: state.account.accountData,
  userPackage: state.account.accountData.userPackage,
  isAdmin: state.account.isAdmin,
  brand: state.app.brand,
});

export default connect(mapStateToProps, { setAlert })(EnergyManagement);
